import { AssistantLanguage, AssistantPageContext, AssistantProposalSummary } from '@/types/assistant'

/** The assistant's own UI text in English and Bangla (its replies follow the same choice). */
export const ASSISTANT_TEXT = {
  en: {
    name: 'Assistant',
    emptyTitle: 'Where should we begin?',
    retry: 'Try again',
    resize: 'Resize the assistant (drag, or use the arrow keys)',
    setupTitle: 'Finish setting up your store',
    setupProgress: (done: number, total: number) => `${done} of ${total} done`,
    setupStart: 'Start',
    setupAsk: 'Set it up with me',
    refusedTry: 'You could ask:',
    jumpToLatest: 'Jump to latest',
    stepsDone: (n: number) => `${n} step${n === 1 ? '' : 's'}`,
    download: 'Download',
    openImage: 'Open full size',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    useAsProductImage: 'Create a product with this image',
    placeholder: 'Ask about your store, or tell me what to do',
    recents: 'Recents',
    newChat: 'New chat',
    noRecents: 'No conversations yet',
    expand: 'Expand',
    collapse: 'Collapse',
    close: 'Close',
    open: 'Open assistant',
    send: 'Send',
    stop: 'Stop',
    attach: 'More',
    generateImage: 'Generate an image',
    imagePrompt: 'Describe the product photo or banner',
    generate: 'Generate',
    voice: 'Voice input',
    listening: 'Listening… tap to stop',
    transcribing: 'Turning your voice into text…',
    working: 'Working on it',
    copy: 'Copy',
    copied: 'Copied',
    helpful: 'Helpful',
    notHelpful: 'Not helpful',
    storeChanges: 'Store changes',
    review: 'Review',
    reviewing: 'Reviewing',
    published: 'Saved',
    discarded: 'Discarded',
    changes: (n: number) => `${n} change${n === 1 ? '' : 's'}`,
    reviewBanner: 'Check the change, then save it when it looks good.',
    reviewBannerTheme: 'Check the change in the preview, then publish when it looks good.',
    discard: 'Discard',
    language: 'Language',
    deleteChat: 'Delete conversation',
    off: 'The assistant is turned off right now.',
    errorGeneric: 'Something went wrong. Please try again.',
    micDenied: 'Microphone access was blocked. Allow it in your browser to use voice input.',
    homeHeading: (store: string) => `What can I help with in ${store}?`,
    homeSetup: (done: number, total: number) => `${done} of ${total} setup steps done`,
    generateText: 'Generate text',
    generating: 'Writing…',
    needTitle: 'Add a product title first.',
    steps: {
      search_products: 'Looking up products', get_product: 'Reading the product', get_stock_alerts: 'Checking stock',
      list_categories: 'Reading categories', get_sales_summary: 'Reading sales', get_top_products: 'Finding best sellers',
      get_order_status_breakdown: 'Reading orders', list_orders: 'Reading orders', get_order: 'Reading the order',
      find_customers: 'Looking up customers', get_customer_report: 'Reading customers', get_analytics_overview: 'Reading analytics',
      get_funnel: 'Reading the funnel', get_product_analytics: 'Reading product analytics', get_traffic_sources: 'Reading traffic',
      get_page_performance: 'Checking page speed', get_storefront_errors: 'Checking errors', get_section_performance: 'Reading section stats',
      get_theme_change_impact: 'Comparing theme changes', get_analytics_signals: 'Reading alerts', navigate: 'Opening the page',
      highlight: 'Pointing it out', open_theme_editor: 'Opening the theme editor', get_setup_status: 'Checking store setup',
      get_theme_summary: 'Reading your theme', get_section_options: 'Reading theme sections', propose_create_product: 'Preparing the product',
      propose_update_product: 'Preparing the changes', propose_create_coupon: 'Preparing the discount', propose_theme_change: 'Preparing theme changes',
      propose_stock_update: 'Preparing the stock change', generate_product_image: 'Creating the image',
    } as Record<string, string>,
  },
  bn: {
    name: 'অ্যাসিস্ট্যান্ট',
    emptyTitle: 'কোথা থেকে শুরু করব?',
    retry: 'আবার চেষ্টা করুন',
    resize: 'অ্যাসিস্ট্যান্টের আকার বদলান (টেনে, বা অ্যারো কী দিয়ে)',
    setupTitle: 'আপনার স্টোর সেটআপ শেষ করুন',
    setupProgress: (done: number, total: number) => `${total}টির মধ্যে ${done}টি শেষ`,
    setupStart: 'শুরু করুন',
    setupAsk: 'আমার সাথে সেটআপ করুন',
    refusedTry: 'জিজ্ঞেস করতে পারেন:',
    jumpToLatest: 'সর্বশেষে যান',
    stepsDone: (n: number) => `${n}টি ধাপ`,
    download: 'ডাউনলোড',
    openImage: 'পূর্ণ আকারে দেখুন',
    copyLink: 'লিংক কপি',
    linkCopied: 'লিংক কপি হয়েছে',
    useAsProductImage: 'এই ছবি দিয়ে পণ্য তৈরি করুন',
    placeholder: 'আপনার স্টোর নিয়ে জিজ্ঞেস করুন, বা কী করতে হবে বলুন',
    recents: 'সাম্প্রতিক',
    newChat: 'নতুন চ্যাট',
    noRecents: 'এখনো কোনো কথোপকথন নেই',
    expand: 'বড় করুন',
    collapse: 'ছোট করুন',
    close: 'বন্ধ করুন',
    open: 'অ্যাসিস্ট্যান্ট খুলুন',
    send: 'পাঠান',
    stop: 'থামান',
    attach: 'আরও',
    generateImage: 'ছবি তৈরি করুন',
    imagePrompt: 'পণ্যের ছবি বা ব্যানারের বর্ণনা দিন',
    generate: 'তৈরি করুন',
    voice: 'ভয়েস ইনপুট',
    listening: 'শুনছি… থামাতে চাপুন',
    transcribing: 'আপনার কথা লেখায় রূপান্তর করছি…',
    working: 'কাজ করছি',
    copy: 'কপি',
    copied: 'কপি হয়েছে',
    helpful: 'উপকারী',
    notHelpful: 'উপকারী নয়',
    storeChanges: 'স্টোরের পরিবর্তন',
    review: 'দেখুন',
    reviewing: 'দেখা হচ্ছে',
    published: 'সেভ হয়েছে',
    discarded: 'বাতিল',
    changes: (n: number) => `${n}টি পরিবর্তন`,
    reviewBanner: 'পরিবর্তনটি দেখে নিন, ঠিক থাকলে সেভ করুন।',
    reviewBannerTheme: 'প্রিভিউতে পরিবর্তনটি দেখে নিন, ঠিক থাকলে পাবলিশ করুন।',
    discard: 'বাতিল',
    language: 'ভাষা',
    deleteChat: 'কথোপকথন মুছুন',
    off: 'অ্যাসিস্ট্যান্ট এখন বন্ধ আছে।',
    errorGeneric: 'কিছু একটা ভুল হয়েছে। আবার চেষ্টা করুন।',
    micDenied: 'মাইক্রোফোন ব্যবহারের অনুমতি নেই। ভয়েস ইনপুটের জন্য ব্রাউজারে অনুমতি দিন।',
    homeHeading: (store: string) => `${store}-এ আজ কী সাহায্য করতে পারি?`,
    homeSetup: (done: number, total: number) => `সেটআপের ${total}টি ধাপের ${done}টি শেষ`,
    generateText: 'লেখা তৈরি করুন',
    generating: 'লিখছি…',
    needTitle: 'আগে পণ্যের নাম দিন।',
    steps: {
      search_products: 'পণ্য খুঁজছি', get_product: 'পণ্যটি পড়ছি', get_stock_alerts: 'স্টক দেখছি',
      list_categories: 'ক্যাটাগরি দেখছি', get_sales_summary: 'বিক্রি দেখছি', get_top_products: 'সেরা বিক্রিত পণ্য খুঁজছি',
      get_order_status_breakdown: 'অর্ডার দেখছি', list_orders: 'অর্ডার দেখছি', get_order: 'অর্ডারটি পড়ছি',
      find_customers: 'গ্রাহক খুঁজছি', get_customer_report: 'গ্রাহকদের তথ্য দেখছি', get_analytics_overview: 'অ্যানালিটিক্স দেখছি',
      get_funnel: 'কেনার ধাপগুলো দেখছি', get_product_analytics: 'পণ্যের অ্যানালিটিক্স দেখছি', get_traffic_sources: 'ভিজিটর কোথা থেকে আসে দেখছি',
      get_page_performance: 'পেজের গতি দেখছি', get_storefront_errors: 'এরর দেখছি', get_section_performance: 'সেকশনের তথ্য দেখছি',
      get_theme_change_impact: 'থিম পরিবর্তনের প্রভাব দেখছি', get_analytics_signals: 'সতর্কবার্তা দেখছি', navigate: 'পেজটি খুলছি',
      highlight: 'দেখিয়ে দিচ্ছি', open_theme_editor: 'থিম এডিটর খুলছি', get_setup_status: 'স্টোর সেটআপ দেখছি',
      get_theme_summary: 'আপনার থিম দেখছি', get_section_options: 'থিমের সেকশন দেখছি', propose_create_product: 'পণ্যটি প্রস্তুত করছি',
      propose_update_product: 'পরিবর্তনগুলো প্রস্তুত করছি', propose_create_coupon: 'ডিসকাউন্ট প্রস্তুত করছি', propose_theme_change: 'থিমের পরিবর্তন প্রস্তুত করছি',
      propose_stock_update: 'স্টকের পরিবর্তন প্রস্তুত করছি', generate_product_image: 'ছবি তৈরি করছি',
    } as Record<string, string>,
  },
} as const

export const assistantText = (language: AssistantLanguage) => ASSISTANT_TEXT[language]

/** The label for a tool step; Bangla falls back to English names for the progress lines. */
export const stepLabel = (language: AssistantLanguage, tool: string) =>
  ASSISTANT_TEXT[language].steps[tool] ?? ASSISTANT_TEXT.en.steps[tool] ?? ASSISTANT_TEXT[language].working

/** Starter prompts, each shown only when the staff member has the tool it needs. */
export const SUGGESTIONS: { tool: string; en: string; bn: string }[] = [
  { tool: 'get_setup_status', en: 'Help me finish setting up my store', bn: 'আমার স্টোর সেটআপ শেষ করতে সাহায্য করুন' },
  { tool: 'propose_create_product', en: 'Create a product listing', bn: 'একটি পণ্যের লিস্টিং তৈরি করুন' },
  { tool: 'get_sales_summary', en: 'How are my sales today?', bn: 'আজ আমার বিক্রি কেমন?' },
  { tool: 'get_stock_alerts', en: 'What is running out of stock?', bn: 'কোন পণ্যের স্টক শেষ হয়ে যাচ্ছে?' },
  { tool: 'get_product_analytics', en: 'Which products do people view but not buy?', bn: 'কোন পণ্য মানুষ দেখে কিন্তু কেনে না?' },
  { tool: 'get_page_performance', en: 'Is my store fast enough?', bn: 'আমার স্টোর কি যথেষ্ট দ্রুত?' },
  { tool: 'generate_product_image', en: 'Generate product images', bn: 'পণ্যের ছবি তৈরি করুন' },
  { tool: 'propose_theme_change', en: 'Help me improve my home page', bn: 'আমার হোম পেজ উন্নত করতে সাহায্য করুন' },
]

/** Which starter prompts fit a screen best (path prefix → tools), shown first there. */
export const PAGE_SUGGESTION_TOOLS: [string, string[]][] = [
  ['/products', ['propose_create_product', 'get_stock_alerts', 'get_product_analytics', 'generate_product_image']],
  ['/inventory', ['get_stock_alerts']],
  ['/orders', ['get_sales_summary']],
  ['/analytics', ['get_product_analytics', 'get_page_performance', 'get_sales_summary']],
  ['/reports', ['get_sales_summary', 'get_product_analytics']],
  ['/themes', ['propose_theme_change', 'get_page_performance']],
  ['/', ['get_setup_status', 'get_sales_summary', 'propose_create_product']],
]

// Dashboard paths → the assistant's route ids (server: copilot/tools/guide.tool.ts DASHBOARD_ROUTES).
const ROUTE_PATTERNS: [RegExp, string, AssistantPageContext['entity_type']?][] = [
  [/^\/$/, 'dashboard'],
  [/^\/products\/new$/, 'products.new'],
  [/^\/products\/([0-9a-f]{24})$/, 'products.detail', 'product'],
  [/^\/products$/, 'products'],
  [/^\/orders\/new$/, 'orders.new'],
  [/^\/orders\/methods$/, 'settings.delivery_methods'],
  [/^\/orders\/locations$/, 'settings.delivery_areas'],
  [/^\/orders\/([0-9a-f]{24})$/, 'orders.detail', 'order'],
  [/^\/orders$/, 'orders'],
  [/^\/customers\/([0-9a-f]{24})$/, 'customers.detail', 'customer'],
  [/^\/customers$/, 'customers'],
  [/^\/categories$/, 'categories'],
  [/^\/brands$/, 'brands'],
  [/^\/attributes$/, 'attributes'],
  [/^\/inventory$/, 'inventory'],
  [/^\/reviews$/, 'reviews'],
  [/^\/coupons$/, 'coupons'],
  [/^\/campaigns/, 'campaigns'],
  [/^\/blog/, 'blog'],
  [/^\/media$/, 'media'],
  [/^\/analytics$/, 'analytics'],
  [/^\/reports\/sales$/, 'reports.sales'],
  [/^\/reports\/revenue$/, 'reports.revenue'],
  [/^\/reports\/inventory$/, 'reports.inventory'],
  [/^\/reports\/customers$/, 'reports.customers'],
  [/^\/themes$/, 'themes'],
  [/^\/settings$/, 'settings'],
  [/^\/system\/payment-gateways$/, 'settings.payments'],
  [/^\/shipping$/, 'settings.shipping'],
  [/^\/system\/sms-gateways$/, 'settings.sms'],
  [/^\/system\/administration$/, 'settings.users'],
  [/^\/system\/roles$/, 'settings.roles'],
  [/^\/profile$/, 'profile'],
]

/** Ids only: the route and the record open on it. Form contents never leave the page. */
export const pageContextFor = (pathname: string, search: string): AssistantPageContext => {
  for (const [pattern, routeId, entityType] of ROUTE_PATTERNS) {
    const match = pathname.match(pattern)
    if (!match) continue
    const context: AssistantPageContext = { route_id: routeId }
    if (entityType && match[1]) {
      context.entity_type = entityType
      context.entity_id = match[1]
    }
    if (routeId === 'themes') {
      const page = new URLSearchParams(search).get('page')
      if (page) context.theme_page = page
    }
    return context
  }
  return {}
}

/** Paths for the route ids a setup checklist links to (non-detail routes only). */
export const ROUTE_PATHS: Record<string, string> = {
  'dashboard': '/', 'products': '/products', 'products.new': '/products/new', 'categories': '/categories', 'brands': '/brands',
  'attributes': '/attributes', 'inventory': '/inventory', 'reviews': '/reviews', 'orders': '/orders', 'orders.new': '/orders/new',
  'customers': '/customers', 'coupons': '/coupons', 'campaigns': '/campaigns', 'blog': '/blog', 'media': '/media',
  'analytics': '/analytics', 'reports.sales': '/reports/sales', 'reports.revenue': '/reports/revenue',
  'reports.inventory': '/reports/inventory', 'reports.customers': '/reports/customers', 'themes': '/themes',
  'settings': '/settings', 'settings.localization': '/settings?tab=localization', 'settings.domains': '/settings?tab=domains',
  'settings.payments': '/system/payment-gateways', 'settings.delivery_methods': '/orders/methods',
  'settings.delivery_areas': '/orders/locations', 'settings.shipping': '/shipping', 'settings.sms': '/system/sms-gateways',
  'settings.users': '/system/administration', 'settings.roles': '/system/roles', 'profile': '/profile',
}

/** Where a proposal is reviewed: the normal screen for that change, prefilled from the proposal. */
export const reviewPathFor = (proposal: Pick<AssistantProposalSummary, 'kind' | 'proposal_uid' | 'target_id'>) => {
  const q = `proposal=${proposal.proposal_uid}`
  switch (proposal.kind) {
    case 'product_create': return `/products/new?${q}`
    case 'product_update': return `/products/${proposal.target_id}?${q}`
    case 'stock_update': return `/products/${proposal.target_id}?${q}`
    case 'coupon_create': return `/coupons?${q}`
    case 'theme_change': return `/themes?${q}`
  }
}

/** Setup steps (server: store_setup.service) in the merchant's language, with a one-line hint. */
export const SETUP_STEP_TEXT: Record<string, { en: [string, string]; bn: [string, string] }> = {
  store_details: { en: ['Store details', 'Name, email, phone and address customers see.'], bn: ['স্টোরের তথ্য', 'গ্রাহকরা যে নাম, ইমেইল, ফোন ও ঠিকানা দেখবে।'] },
  logo: { en: ['Add your logo', 'Show your brand in the header.'], bn: ['লোগো যোগ করুন', 'হেডারে আপনার ব্র্যান্ড দেখান।'] },
  first_product: { en: ['Add your first product', 'Start with a title, price and a photo.'], bn: ['প্রথম পণ্য যোগ করুন', 'নাম, দাম ও একটি ছবি দিয়ে শুরু করুন।'] },
  payments: { en: ['Set up payments', 'Let customers pay by card, mobile banking or cash.'], bn: ['পেমেন্ট সেটআপ করুন', 'কার্ড, মোবাইল ব্যাংকিং বা ক্যাশে পেমেন্ট নিন।'] },
  delivery: { en: ['Set up delivery', 'Delivery methods and the areas you deliver to.'], bn: ['ডেলিভারি সেটআপ করুন', 'ডেলিভারির পদ্ধতি ও এলাকা।'] },
  home_page: { en: ['Design your home page', 'Pick sections that fit your store.'], bn: ['হোম পেজ সাজান', 'আপনার স্টোরের জন্য সেকশন বেছে নিন।'] },
  hero_image: { en: ['Add a home banner picture', 'Put your own photo in the first banner.'], bn: ['হোম ব্যানারে ছবি দিন', 'প্রথম ব্যানারে নিজের ছবি দিন।'] },
  menu: { en: ['Set up your menu', 'Help customers find their way around.'], bn: ['মেনু সেটআপ করুন', 'গ্রাহকদের সহজে খুঁজে পেতে সাহায্য করুন।'] },
  domain: { en: ['Connect your domain', 'Optional: use your own web address.'], bn: ['নিজের ডোমেইন যুক্ত করুন', 'ঐচ্ছিক: নিজের ওয়েব ঠিকানা ব্যবহার করুন।'] },
}
